import { useCallback } from 'react';

import upperFirst from 'lodash/upperFirst';

import { useAlert } from '@spotify/confidence-core-react';
import {
  getError,
  getTypeOrNull,
  useTransitionWorkflowInstanceMutation,
} from '@spotify/confidence-graphql';

import { pendingTransitionUtils } from '../../components/WorkflowInstanceState';
import { useWorkflowTransitionProgressDialog } from '../../contexts/WorkflowTransitionProgressContext/WorkflowTransitionProgressContext';
import { getWorkflowTransitionProgress } from '../useWorkflowTransitionProgress';

type UseTransitionWorkflowInstanceArgs = {
  workflowInstanceName?: string;
  onTransitionCompleted?: (state: string) => void;
};

type UseTransitionWorkflowInstance = {
  performTransition: (
    targetState?: string | null,
    parameters?: Record<string, unknown>,
  ) => Promise<void>;
  loading: boolean;
  error?: Error;
};

export const useTransitionWorkflowInstance = ({
  workflowInstanceName,
  onTransitionCompleted,
}: UseTransitionWorkflowInstanceArgs): UseTransitionWorkflowInstance => {
  const alert = useAlert();
  const transitionProgressDialog = useWorkflowTransitionProgressDialog();

  const [transition, mutationData] = useTransitionWorkflowInstanceMutation({
    update: cache => {
      cache.evict({
        id: cache.identify({
          __typename: 'WorkflowV1WorkflowInstance',
          name: workflowInstanceName,
        }),
      });
      cache.gc();
    },
    onCompleted: ({ transitionWorkflowInstance }) => {
      const response = getTypeOrNull(transitionWorkflowInstance);
      const transitionError = getError(transitionWorkflowInstance);
      if (response) {
        const { workflowInstance } = response;
        const { state, pendingTransition } = workflowInstance || {};
        const transitionAction =
          workflowInstance &&
          getWorkflowTransitionProgress(workflowInstance)?.action;
        if (transitionProgressDialog && transitionAction) {
          transitionProgressDialog.showTransitionProgress(transitionAction);
        } else if (pendingTransition) {
          if (pendingTransitionUtils.hasFailingSteps(pendingTransition)) {
            alert.post({
              message: `${pendingTransition.failedSteps.length} step${
                pendingTransition.failedSteps.length === 1 ? '' : 's'
              } failed when trying to ${
                pendingTransition.transitionAction
              }. Find details and retry in the Status section`,
              severity: 'error',
            });
          } else {
            alert.post({
              message: `${upperFirst(
                pendingTransition.transitionAction,
              )} in progress`,
              severity: 'info',
            });
          }
        } else if (state) {
          onTransitionCompleted?.(state);
        }
      } else if (transitionError) {
        alert.post({
          message: transitionError.message,
          severity: 'error',
        });
      }
    },
    onError: error => {
      alert.post({
        message: error.message,
        severity: 'error',
      });
    },
  });

  const performTransition = useCallback(
    async (
      targetState?: string | null,
      parameters?: Record<string, unknown>,
    ) => {
      if (workflowInstanceName && targetState) {
        try {
          await transition({
            variables: {
              name: workflowInstanceName,
              action: targetState,
              asyncTransition: true,
              parameters,
            },
          });
        } catch {
          // Error is handled by the mutation's onError callback
        }
      }
    },
    [transition, workflowInstanceName],
  );

  return { ...mutationData, performTransition };
};
